import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { guardarExtracto, previsualizarExtracto, previsualizarExtractoTexto } from "../services/egresosService";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 32 },
  "& .MuiInputBase-input": { py: "5px", fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};
const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };

// Importa el extracto del banco desde Excel (espejo de importar.php > "Registrar Extracto" y cargaDatos.php):
// 1) elegir archivo y mes, 2) revisar la vista previa, 3) confirmar y guardar.
export default function ExtractoImportarDialog({ open, onClose, mes: mesInicial, year: yearInicial, onGuardado }) {
  const [modo, setModo] = useState("archivo");
  const [texto, setTexto] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [mes, setMes] = useState(mesInicial);
  const [year, setYear] = useState(yearInicial);
  const [previa, setPrevia] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setArchivo(null); setTexto(""); setModo("archivo"); setPrevia(null); setError(""); setMes(mesInicial); setYear(yearInicial); }
  }, [open, mesInicial, yearInicial]);

  const anios = Array.from({ length: new Date().getFullYear() + 2 - 2016 }, (_, i) => 2016 + i);

  const visualizar = async () => {
    setCargando(true);
    setError("");
    setPrevia(null);
    try {
      const { data } = modo === "texto" ? await previsualizarExtractoTexto(texto, mes, year) : await previsualizarExtracto(archivo, mes, year);
      setPrevia(data);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo leer el archivo.");
    } finally {
      setCargando(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      const { data } = await guardarExtracto({ mes: previa.mes, year: previa.year, rows: previa.items.filter((i) => !i.error) });
      onGuardado(`Extracto de ${data.periodo}: ${data.guardados} movimiento(s) guardado(s)${data.omitidos ? `, ${data.omitidos} omitido(s)` : ""}.`, previa.mes, previa.year);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo guardar el extracto.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={previa ? "xl" : "sm"} fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Registrar extracto</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        <ToggleButtonGroup exclusive size="small" value={modo} onChange={(_, v) => { if (v) { setModo(v); setPrevia(null); setError(""); } }} sx={{ mb: 1 }}>
          <ToggleButton value="archivo" sx={{ textTransform: "none", py: 0.25 }}>Cargar archivo (.xlsx)</ToggleButton>
          <ToggleButton value="texto" sx={{ textTransform: "none", py: 0.25 }}>Pegar desde Excel</ToggleButton>
        </ToggleButtonGroup>
        {modo === "texto" && (
          <TextField
            multiline minRows={5} maxRows={10} fullWidth size="small" value={texto} onChange={(e) => { setTexto(e.target.value); setPrevia(null); }}
            placeholder="Copia las celdas en Excel (Ctrl+C) y pégalas aquí (Ctrl+V), sin encabezado"
            sx={{ ...fieldSx, mb: 1, "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.72rem", whiteSpace: "pre", overflowX: "auto" } }}
            helperText={texto.trim() ? `${texto.split(/\r?\n/).filter((l) => l.trim()).length} fila(s) pegada(s)` : " "}
          />
        )}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
          {modo === "archivo" && (
            <Button size="small" variant="outlined" component="label">
              {archivo ? archivo.name : "Elegir archivo"}
              <input hidden type="file" accept=".xlsx" onChange={(e) => { setArchivo(e.target.files?.[0] || null); setPrevia(null); }} />
            </Button>
          )}
          <TextField select size="small" label="Mes" value={mes} onChange={(e) => { setMes(Number(e.target.value)); setPrevia(null); }} sx={{ ...fieldSx, width: 140 }}>
            {MESES.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Año" value={year} onChange={(e) => { setYear(Number(e.target.value)); setPrevia(null); }} sx={{ ...fieldSx, width: 100 }}>
            {anios.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <Button size="small" variant="contained" disabled={(modo === "texto" ? !texto.trim() : !archivo) || cargando} onClick={visualizar}
            startIcon={cargando ? <CircularProgress size={14} color="inherit" /> : null}>
            Visualizar
          </Button>
        </Box>
        <Typography sx={{ fontSize: 11 }} color="text.secondary">
          Columnas del Excel (o de lo que pegues), sin encabezado: A fecha, B oficina, C descripción, D referencia, E cód. transacción, F ITF, G débito, H crédito, I banco, J adicional.
          Para BNB dólares el ITF debe ir en 0.
        </Typography>

        {previa && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>
              {previa.validos} de {previa.items.length} fila(s) se pueden guardar. Las filas en rojo se omiten.
            </Typography>
            <TableContainer sx={{ maxHeight: "50vh" }}>
              <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    {["N", "Fecha", "Oficina", "Descripción", "Referencia", "Cód. transacción", "ITF", "Débito", "Crédito", "Banco", "Adicional", "Observación"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previa.items.map((r) => (
                    <TableRow key={r.n} sx={{ bgcolor: r.error ? "error.light" : "success.light", "& .MuiTableCell-root": { color: "#1a1a1a" } }}>
                      <TableCell>{r.n}</TableCell><TableCell sx={{ whiteSpace: "nowrap" }}>{r.fecha}</TableCell><TableCell>{r.oficina}</TableCell>
                      <TableCell>{r.descripcion}</TableCell><TableCell>{r.referencia}</TableCell><TableCell>{r.cod_transaccion}</TableCell>
                      <TableCell>{r.itf}</TableCell><TableCell align="right">{r.debito}</TableCell><TableCell align="right">{r.credito}</TableCell>
                      <TableCell>{r.banco}</TableCell><TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.adicional}>{r.adicional}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{r.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose}>Cerrar</Button>
        {previa && (
          <Button size="small" variant="contained" disabled={guardando || !previa.validos} onClick={guardar}>
            {guardando ? <CircularProgress size={16} color="inherit" /> : `Confirmar y guardar (${previa.validos})`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
